import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  canAccessRecordByLevel,
  isPermissionAllowed,
  normalizeApiPermissions,
  normalizePermissionLevel,
  PERMISSION_LEVEL,
} from '../utils/permissions';
import {
  inferMenuItemAction,
  LEGACY_PERMISSION_MAP,
  MENU_MODULE_ALIASES,
  RESOURCE_MODULE_MAP,
  resolveRouteModule,
} from '../utils/permissionMaps';
import { isLoginSubmenuAllowed, isLoginUiModuleActive } from '../utils/loginModuleMenus';
import { setApiPermissionRegistrar } from '../utils/apiPermissionBridge';

const PermissionsContext = createContext();

/** @deprecated — سطوح عددی؛ API جدید رشته‌ای است */
export const PERMISSION_TYPES = {
  ADDED: 1,
  OWNED: 2,
  BOTH: 3,
  ALL: 4,
  NONE: 5,
};

export const MODULE_MAP = {
  clients: 1,
  employees: 2,
  projects: 3,
  tasks: 4,
  attendance: 5,
  leaves: 6,
  timelogs: 7,
  payments: 8,
  invoices: 9,
  estimates: 10,
  expenses: 11,
  contracts: 12,
  events: 13,
  leads: 14,
  holidays: 15,
  products: 16,
  orders: 17,
  tickets: 18,
  notices: 19,
  messages: 20,
  knowledgebase: 21,
  reports: 22,
  settings: 23,
  asset: 24,
  recruit: 25,
  payroll: 26,
  bankaccount: 27,
  letter: 28,
  performance: 29,
  purchase: 30,
  webhooks: 31,
  callcenter: 32,
  shift: 33,
  dashboards: 34,
};

/** @deprecated — مسیرها اکنون با ماژول فعال شرکت کنترل می‌شوند */
export const ROUTE_PERMISSIONS = {};

export const MENU_PERMISSIONS = {
  leads: { module: 'leads' },
  customers: { module: 'clients' },
  products: { module: 'products' },
  orders: { module: 'orders' },
  tickets: { module: 'tickets' },
  callcenter: { module: 'callcenter' },
  notices: { module: 'notices' },
  invoices: { module: 'invoices' },
  payments: { module: 'payments' },
  calendar: { module: 'events' },
  hr: { module: 'employees' },
  action: { module: null },
  settings: { module: null },
};

export const HR_SUBMENU_PERMISSIONS = {};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

const stringLevelToLegacyNumber = (level) => {
  const l = normalizePermissionLevel(level);
  switch (l) {
    case PERMISSION_LEVEL.ADDED:
      return PERMISSION_TYPES.ADDED;
    case PERMISSION_LEVEL.OWNED:
      return PERMISSION_TYPES.OWNED;
    case PERMISSION_LEVEL.BOTH:
      return PERMISSION_TYPES.BOTH;
    case PERMISSION_LEVEL.ALL:
      return PERMISSION_TYPES.ALL;
    default:
      return PERMISSION_TYPES.NONE;
  }
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [resourcePermissions, setResourcePermissionsState] = useState({});
  const [permissionsRevision, setPermissionsRevision] = useState(0);

  useEffect(() => {
    setApiPermissionRegistrar((resourceKey, permissions) => {
      setResourcePermissionsState((prev) => ({
        ...prev,
        [resourceKey]: normalizeApiPermissions(permissions),
      }));
      setPermissionsRevision((r) => r + 1);
    });
    return () => setApiPermissionRegistrar(null);
  }, []);

  const isAdmin = useMemo(
    () => user?.roles?.some((role) => role.name === 'admin') || false,
    [user],
  );

  const isSuperAdmin = useMemo(() => {
    const byFlag =
      user?.is_superadmin === 1 || user?.is_superadmin === '1' || user?.is_superadmin === true;
    const byRole =
      user?.roles?.some((role) => String(role?.name || '').toLowerCase() === 'superadmin') || false;
    return byFlag || byRole;
  }, [user]);

  const activeModules = useMemo(() => user?.modules || [], [user]);
  const moduleMenus = useMemo(() => user?.moduleMenus ?? null, [user?.moduleMenus]);

  const setResourcePermissions = useCallback((resourceKey, permissions) => {
    if (!resourceKey || !permissions) return;
    setResourcePermissionsState((prev) => ({
      ...prev,
      [resourceKey]: normalizeApiPermissions(permissions),
    }));
    setPermissionsRevision((r) => r + 1);
  }, []);

  const getResourcePermissions = useCallback(
    (resourceKey) => resourcePermissions[resourceKey] || null,
    [resourcePermissions],
  );

  const isModuleActive = useCallback(
    (moduleName) => {
      if (!moduleName) return true;
      if (isSuperAdmin || isAdmin) return true;

      if (moduleMenus && typeof moduleMenus === 'object' && !Array.isArray(moduleMenus)) {
        return isLoginUiModuleActive(moduleName, moduleMenus, activeModules);
      }

      if (!activeModules?.length) return false;

      if (activeModules.includes(moduleName)) return true;

      const aliases = MENU_MODULE_ALIASES[moduleName];
      if (aliases?.length) {
        return aliases.some((m) => activeModules.includes(m));
      }

      return false;
    },
    [activeModules, isAdmin, isSuperAdmin, moduleMenus],
  );

  const getActionLevel = useCallback(
    (resourceKey, action) => {
      if (isSuperAdmin || isAdmin) return PERMISSION_LEVEL.ALL;
      return getResourcePermissions(resourceKey)?.[action] ?? null;
    },
    [getResourcePermissions, isAdmin, isSuperAdmin],
  );

  const hasPermission = useCallback(
    (permissionName) => {
      if (!permissionName) return true;
      if (isSuperAdmin || isAdmin) return true;

      const mapped = LEGACY_PERMISSION_MAP[permissionName];
      if (!mapped) return false;

      const moduleKey = RESOURCE_MODULE_MAP[mapped.resource] ?? mapped.resource;
      const actionType = inferMenuItemAction(permissionName);

      // وقتی modules از لاگین آمده، برای دسترسی view همان مرجع است
      if (
        moduleMenus &&
        actionType === 'view' &&
        isLoginUiModuleActive(moduleKey, moduleMenus, activeModules)
      ) {
        return true;
      }

      const level = getActionLevel(mapped.resource, mapped.action);
      if (level != null) {
        return isPermissionAllowed(level);
      }

      if (moduleKey && isModuleActive(moduleKey)) {
        if (actionType === 'view') return true;
      }

      return false;
    },
    [getActionLevel, isAdmin, isModuleActive, isSuperAdmin, moduleMenus, activeModules],
  );

  const getPermissionLevel = useCallback(
    (permissionName) => {
      if (!permissionName) return PERMISSION_TYPES.ALL;
      const mapped = LEGACY_PERMISSION_MAP[permissionName];
      if (!mapped) return PERMISSION_TYPES.NONE;
      const level = getActionLevel(mapped.resource, mapped.action);
      if (level == null) return PERMISSION_TYPES.NONE;
      return stringLevelToLegacyNumber(level);
    },
    [getActionLevel],
  );

  const hasFullAccess = useCallback(
    (permissionName) => getPermissionLevel(permissionName) === PERMISSION_TYPES.ALL,
    [getPermissionLevel],
  );

  const canAccessMenu = useCallback(
    (menuKey) => {
      const menuConfig = MENU_PERMISSIONS[menuKey];
      if (!menuConfig) return true;
      if (menuKey === 'action') return true;
      if (menuKey === 'settings') return isAdmin || isSuperAdmin;
      if (menuConfig.module && !isModuleActive(menuConfig.module)) return false;
      return true;
    },
    [isAdmin, isModuleActive, isSuperAdmin],
  );

  const canAccessRoute = useCallback(
    (path) => {
      if (isSuperAdmin || isAdmin) return true;
      const moduleName = resolveRouteModule(path);
      if (moduleName === null) return true;
      if (moduleName === undefined) return true;
      return isModuleActive(moduleName);
    },
    [isAdmin, isModuleActive, isSuperAdmin],
  );

  const canAccessRecord = useCallback(
    (permissionName, recordOwnerId = null, recordAddedById = null) => {
      if (isSuperAdmin || isAdmin) return true;
      const mapped = LEGACY_PERMISSION_MAP[permissionName];
      if (!mapped) return false;
      const level = getActionLevel(mapped.resource, mapped.action);
      if (!level) return false;
      return canAccessRecordByLevel(level, {
        userId: user?.id,
        ownerId: recordOwnerId,
        addedById: recordAddedById,
      });
    },
    [getActionLevel, isAdmin, isSuperAdmin, user?.id],
  );

  const canShowMenuItem = useCallback(
    (permissionName) => {
      if (!permissionName) return true;
      if (isSuperAdmin || isAdmin) return true;

      const actionType = inferMenuItemAction(permissionName);
      if (actionType === 'view') {
        return hasPermission(permissionName);
      }

      return hasPermission(permissionName);
    },
    [hasPermission, isAdmin, isSuperAdmin],
  );

  const filterMenuItems = useCallback(
    (items, menuKey) => {
      if (!items?.length) return [];
      if (isSuperAdmin || isAdmin) return items;
      if (menuKey === 'action') return items;

      const usesLoginMenus =
        moduleMenus && typeof moduleMenus === 'object' && !Array.isArray(moduleMenus);

      return items.filter((item) => {
        if (usesLoginMenus) {
          return isLoginSubmenuAllowed(menuKey, item.path, moduleMenus);
        }
        if (item.path && !canAccessRoute(item.path)) return false;
        if (item.permission && !canShowMenuItem(item.permission)) return false;
        return true;
      });
    },
    [canAccessRoute, canShowMenuItem, isAdmin, isSuperAdmin, moduleMenus],
  );

  const getModulePermissions = useCallback(() => [], []);

  const value = {
    user,
    permissions: resourcePermissions,
    activeModules,
    isAdmin,
    isSuperAdmin,
    hasPermission,
    getPermissionLevel,
    hasFullAccess,
    isModuleActive,
    canAccessMenu,
    canAccessRoute,
    canAccessRecord,
    canShowMenuItem,
    filterMenuItems,
    getModulePermissions,
    setResourcePermissions,
    getResourcePermissions,
    getActionLevel,
    permissionsRevision,
    PERMISSION_TYPES,
    MODULE_MAP,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export default PermissionsContext;
